<template>
  <v-card class="get-deep-id-card" flat rounded="lg">
    <!-- Content container with fixed height to prevent layout shift -->
    <div class="content-wrapper">
      <!-- QR Code Section -->
      <div v-if="qrGenerationStatus === 'success'" class="qr-section">
        <figure
          v-if="deepAdminInternalStore.userValidationURL"
          class="qr-figure"
        >
          <VueQrcode
            :options="qrOptions"
            :value="deepAdminInternalStore.userValidationURL"
          />
          <img alt="deep-id" class="qr-logo" src="/img/qr-logo.png" />
        </figure>

        <a
          class="validation-link"
          :href="deepAdminInternalStore.userValidationURL"
          target="_blank"
        >
          {{ deepAdminInternalStore.userValidationURL }}
        </a>

        <div class="d-flex ga-4">
          <a
            class="text-primary"
            :href="deepIdWebLink"
            @click.prevent="emit('open-web-ident', deepIdWebLink)"
            >{{
              i18n.t('texts.qualified_signature_instructions_deep_id_web_ident')
            }}</a
          >
          <a class="text-primary" :href="deepIdWebLink" target="_blank">{{
            i18n.t(
              'texts.qualified_signature_instructions_deep_id_web_ident_external'
            )
          }}</a>
        </div>

        <p v-if="deepAdminInternalStore.userValidationVoucher" class="voucher">
          {{ deepAdminInternalStore.userValidationVoucher }}
        </p>

        <div class="scan-instructions">
          <v-icon class="scan-icon" color="grey-darken-4" size="24">
            fal fa-mobile
          </v-icon>
          <span class="scan-text">
            {{ i18n.t('hints.scan_qr_code') }}
          </span>
        </div>
      </div>

      <!-- Spinner Section -->
      <div
        v-if="
          qrGenerationStatus === 'verifying' || qrGenerationStatus === 'idle'
        "
        class="center-content"
      >
        <v-progress-circular color="primary" indeterminate size="60" />
      </div>

      <!-- Error Section -->
      <div v-if="qrGenerationStatus === 'error'" class="center-content">
        <v-alert closable title="Error" type="error" variant="tonal">
          {{ i18n.t('text.identity.qr_code.error_message') }}
        </v-alert>
      </div>
    </div>

    <!-- Restart Link -->
    <div v-if="deepAdminInternalStore.userValidationURL" class="action-footer">
      <span class="restart-link" @click="restartVerification">
        {{ i18n.t('actions.new_qrcode') }}
      </span>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import VueQrcode from '@chenfengyuan/vue-qrcode';
import { computed, inject } from 'vue';
import { useEmitter } from '@/composables/useEmitter';
import { useI18n } from 'vue-i18n';
import { useMsgBox } from '@deepcloud/deep-ui-lib';
import type { MsgBoxOptions } from '@deepcloud/deep-ui-lib';
import { qrGenerationStatusKey } from '@/types/QrGenerationStatusKey';
import { useDeepAdminInternalStore } from '@/stores/deepadmin/deepadmin-internal.ts';

const emitter = useEmitter();
const qrGenerationStatus = inject(qrGenerationStatusKey);
const deepAdminInternalStore = useDeepAdminInternalStore();
const i18n = useI18n();
const deepMsgBox = useMsgBox();
const emit = defineEmits<{
  (event: 'open-web-ident', url: string): void;
}>();

async function restartVerification() {
  const msgOptions: MsgBoxOptions = {
    title: i18n.t('text.identity.qr_code.restart_verification_dialog.title'),
    content: i18n.t('text.identity.qr_code.restart_verification_dialog.text'),
    btnActions: {
      confirm: {
        text: i18n.t('actions.generate_qrcode')
      }
    },
    autoCloseOnActionClick: false
  };

  const { action, close } = await deepMsgBox.warning(msgOptions);

  if (action === 'confirm') {
    emitter.$emit('restart-verification');
  }

  close();
}

const deepIdWebLink = computed(() => {
  if (!deepAdminInternalStore.userValidationURL) {
    return '';
  }
  const url = new URL(import.meta.env.VITE_DEEPID_WEB_BASE_URL);
  url.searchParams.set(
    'branch-io-url',
    deepAdminInternalStore.userValidationURL
  );
  return url.toString();
});

const qrOptions = {
  errorCorrectionLevel: 'Q',
  width: 200
};
</script>

<style scoped lang="scss">
.get-deep-id-card {
  background-color: rgb(var(--v-theme-grey-lighten-4));
  padding: 24px;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
}

// QR Code Section
.qr-section {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.qr-figure {
  position: relative;
  margin: 0;
  display: inline-block;

  canvas {
    display: block;
    border-radius: 12px;
  }
}

.qr-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20%;
  height: 20%;
  background-color: white;
  border: 4px solid white;
  border-radius: 4px;
}

.validation-link {
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  text-decoration: none;
  word-break: break-all;
}

.voucher {
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  margin: 0;
}

.scan-instructions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  max-width: 240px;
  margin: 0 auto;
}

.scan-icon {
  flex-shrink: 0;
}

.scan-text {
  font-size: 12px;
  line-height: 16px;
  text-align: left;
}

// Spinner & Error Sections
.center-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.spinner-section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

// Action Footer
.action-footer {
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: 16px;
}

.restart-link {
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
}
</style>
